import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { isNullOrUndefined } from '@sla-ng/core';
import * as moment from 'moment';
import { Moment } from 'moment';
import { debounceTime, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { IProductionLayout } from '../../../models/layout/production-layout.model';
import { IProductionOrder } from '../../../models/production-order.model';
import { IProduction } from '../../../models/production.model';
import { TransactionalDataFacade } from '../../../services/facades/external-data/transactional-data-facade';
import { ITransactionalDataState } from '../../../services/facades/external-data/transactional-data.state.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { ProductionLayoutService } from '../../../services/production-layout.service';

@Component({
    templateUrl: './production.page.html',
    styleUrls: [ './production.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductionPage implements OnInit, AfterViewInit, OnDestroy {


    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public layout: IProductionLayout;

    public IProduction: IProduction;
    public IProductionOrder: IProductionOrder;

    public dataSource: MatTableDataSource<IProductionOrder> = new MatTableDataSource<IProductionOrder>([]);

    public transactionalDataState$: Observable<ITransactionalDataState>;
    public sharedDataState$: Observable<ISharedDataState>;
    public dateFilterFormGroup = this.myFormBuilder.group({
        start: null as moment.Moment,
        end: null as moment.Moment,
    });

    public searchFormControl: FormControl<string> = new FormControl('');

    private destroy$: Subject<void> = new Subject();

    constructor(private myTransactionalDataFacade: TransactionalDataFacade,
                private myFormBuilder: FormBuilder,
                private mySharedDataFacade: SharedDataFacade,
                private myProductionLayoutService: ProductionLayoutService,
                private myRouter: Router,
                private myActivatedRoute: ActivatedRoute) {
    }

    public ngOnInit(): void {
        this.layout = this.myProductionLayoutService.getProductionLayout();
        this.transactionalDataState$ = this.myTransactionalDataFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.transactionalDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ITransactionalDataState) => s.productionOrders))
            .subscribe((data: IProductionOrder[]) => {
                this.dataSource.data = data;
            });

        this.dateFilterFormGroup.valueChanges.pipe(debounceTime(500)).subscribe((daterange: { start: Moment, end: Moment }) => {
            if (!isNullOrUndefined(daterange.start) && !isNullOrUndefined(daterange.end)) {
                this.myTransactionalDataFacade.setDateRange(daterange);
                void this.myTransactionalDataFacade.fetchProductionOrders();
            }
        });

        this.searchFormControl.valueChanges.subscribe((filter: string) => {
            this.applyFilter(filter);
        });

        this.sharedDataState$.pipe(
            map((s: ISharedDataState) => s.costUnitLookup),
            takeUntil(this.destroy$),
        ).subscribe((costUnitLookup) => {

            this.dataSource.filterPredicate = (productionOrder: IProductionOrder, filter: string): boolean => {
                // Filter by productionOrderNr
                if (productionOrder.productionOrderNr.toString().indexOf(filter) !== -1) {
                    return true;
                }

                // Filter by costUnitName or articleNr
                for (const productionExit of productionOrder.exit) {
                    if (!isNullOrUndefined(productionExit.costUnitId)
                        && !isNullOrUndefined(costUnitLookup[ productionExit.costUnitId ])
                        && ((costUnitLookup[ productionExit.costUnitId ].name.trim().toLowerCase().indexOf(filter) !== -1)
                            || (costUnitLookup[ productionExit.costUnitId ].articleNr.trim().toLowerCase().indexOf(filter) !== -1))) {
                        return true;
                    }
                }
                return false;
            };
        });

        // Initialize the datepicker with the daterange from the state
        const state: ITransactionalDataState = this.myTransactionalDataFacade.getStateSnapshot();
        this.dateFilterFormGroup.setValue(state.selectedDateRange);
        // Initialize the textFilter
        this.searchFormControl.setValue(state.productionFilter);
        void firstValueFrom(this.mySharedDataFacade.fetchCostUnits());

    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myTransactionalDataFacade.setProductionFilter(this.searchFormControl.value);
    }

    public openProductionOrder(selectedProductionOrder: IProductionOrder): void {
        // await this.myTransactionalFacade.setSelectedProductionVersion(selectedProductionVersion.id);
        void this.myRouter.navigate([ `./${selectedProductionOrder.productionOrderNr}` ],
            { relativeTo: this.myActivatedRoute });
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }
}
